import { Add, Save } from '@mui/icons-material';
import React, { useEffect, useReducer, useState } from 'react';
import { Box, Button } from '@mui/material';
import { useAuth, useRequest } from 'shared/hooks';
import { getNoteTypes } from 'shared/api/notes';
import { VARIANTS } from 'shared/constants';
import { StaticSearchableSelect } from 'shared/components/select/SearchableSelects';
import notesCreatorReducer, { getInitialState } from './reducer';
import NoteInput from './NoteInput';

/**
 * @param {Object} props - Component props
 * @param {string} props.entityType - Type of entity
 * @param {number} props.entityID - ID of entity
 * @param {Function} props.onAddNotes - Callback when notes are added
 * @param {Function} props.setDirty - Callback to set dirty state
 * @returns {JSX.Element}
 */
export default function NotesCreator({
  entityType,
  entityID,
  onAddNotes,
  setDirty,
}) {
  const tmpNoteTypes = [];
  const [noteTypes, setNoteTypes] = useState(tmpNoteTypes);
  const { request } = useRequest();
  const { user } = useAuth();
  const [{
    notes,
    savable,
  }, dispatch] = useReducer(
    notesCreatorReducer,
    getInitialState(entityType, entityID),
  );

  useEffect(() => {
    setDirty(notes.length > 0 && notes.some((note) => note.note_type?.name || note.value));
  }, [notes, setDirty]);

  useEffect(() => {
    request(getNoteTypes())
      .then((res) => setNoteTypes(res.rows));
  }, [request]);

  const handleAddNoteInput = () => {
    dispatch({
      type: 'added',
      entityType,
      entityID,
    });
  };

  const handleDeleteNoteInput = (i) => {
    dispatch({
      type: 'removed',
      index: i,
      entityType,
      entityID,
    });
  };

  const handleSelectNoteType = (i, noteType) => {
    dispatch({
      type: 'changeLabel',
      index: i,
      noteType,
    });
  };

  const handleChangeValue = (i, value) => {
    dispatch({
      type: 'changeValue',
      index: i,
      value,
    });
  };

  const handleSaveNotes = () => {
    const afterSave = () => {
      // Find any note types that aren't in original list.
      const newNoteTypes = notes.filter((n) => noteTypes
        .findIndex((nt) => nt.name === n.note_type.name) === -1);
      // If any exist, reload note types.
      if (newNoteTypes.length > 0) {
        request(getNoteTypes())
          .then((res) => setNoteTypes(res.rows));
      }
    };

    onAddNotes(
      notes.map((note) => ({
        ...note,
        id: undefined,
        employee_id: user.id,
        note_type: {
          ...note.note_type,
          employee_id: user.id,
        },
      })),
      afterSave,
      dispatch,
      {
        type: 'saved',
        entityType,
        entityID,
      },
    );
  };

  return (
    <Box>
      <Box sx={{ marginBottom: 1, marginTop: notes.length > 0 && 1 }}>
        {notes.map((note, i) => (
          <Box
            key={note.id}
            sx={{ display: 'flex', alignItems: 'start', gap: '0.5rem' }}
          >
            <Box key={`label-${note.id}`} sx={{ flex: '0 0 33%', minWidth: 0 }}>
              <StaticSearchableSelect
                creatable
                onChange={(value) => handleSelectNoteType(i, value)}
                label={`Label ${i + 1}`}
                getOptionValue={(option) => option.id}
                selectedOption={note?.note_type}
                selectValue={note?.note_type}
                staticOptions={noteTypes}
                placeholder="Select a label..."
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <NoteInput
                note={note}
                size="small"
                onChange={(v) => handleChangeValue(i, v)}
                onDeleteNote={() => handleDeleteNoteInput(i)}
                disableDelete={!note.note_type && i === 0}
                creator
              />
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{
        display: 'flex',
        alignContent: 'start',
        justifyContent: 'end',
        gap: 1,
      }}
      >
        <Button
          variant={VARIANTS.outlined}
          startIcon={<Add />}
          onClick={handleAddNoteInput}
        >
          add
        </Button>
        <Button
          variant={VARIANTS.contained}
          startIcon={<Save />}
          disabled={!savable}
          onClick={handleSaveNotes}
        >
          save
        </Button>
      </Box>
    </Box>
  );
}
